import React, { useState, useEffect, useMemo } from "react";
import { Plus, Trash2, UserCog, Search } from "lucide-react";
import { getUsers, createUser, updateUser, deleteUser } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const ROLES = ["user", "vendor", "landlord", "tenant", "buyer", "agent", "admin"];
const roleColor = {
  admin: "bg-purple-100 text-purple-700", agent: "bg-indigo-100 text-indigo-700",
  landlord: "bg-blue-100 text-blue-700", tenant: "bg-green-100 text-green-700",
  vendor: "bg-amber-100 text-amber-700", buyer: "bg-teal-100 text-teal-700", user: "bg-gray-100 text-gray-600",
};

const AdminUsers = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "user" });
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const refresh = () => getUsers().then(setUsers).catch(() => setUsers([]));
  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await createUser(form); toast({ title: "User created" }); setOpen(false); setForm({ name: "", email: "", password: "", phone: "", role: "user" }); refresh(); }
    catch (err) { toast({ title: "Create failed", description: err?.response?.data?.detail || "" }); }
  };

  const changeRole = async (id, role) => {
    try { await updateUser(id, { role }); setUsers((us) => us.map((u) => u.id === id ? { ...u, role } : u)); toast({ title: "Role updated" }); }
    catch { toast({ title: "Update failed" }); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await deleteUser(u.id); toast({ title: "User deleted" }); refresh(); }
    catch (err) { toast({ title: "Delete failed", description: err?.response?.data?.detail || "" }); }
  };

  const filtered = useMemo(() => users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [u.name, u.email, u.phone].filter(Boolean).some((f) => f.toLowerCase().includes(s));
  }), [users, q, roleFilter]);

  const inputCls = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-users">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><UserCog size={19} className="text-[#b8935f]" /> User Management ({users.length})</h3>
        <button onClick={() => setOpen(true)} data-testid="add-user-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Plus size={15} /> Add User</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0a0]" />
          <input placeholder="Search by name, email or phone…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full h-[44px] pl-10 pr-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" data-testid="user-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[160px] h-[44px] border-[#e2d8c8] capitalize" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? <p className="p-8 text-center text-[#8a8a8a]">No users match.</p> : filtered.map((u) => (
          <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]" data-testid="user-row">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#b8935f] text-white flex items-center justify-center text-[13px] font-500">{u.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>
              <div><p className="text-[14px] font-500 text-[#2b2b2b]">{u.name}</p><p className="text-[12px] text-[#a0a0a0]">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p></div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${roleColor[u.role]}`}>{u.role}</span>
              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                <SelectTrigger className="w-[120px] h-9 border-[#e2d8c8]"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => remove(u)} data-testid="delete-user-btn" className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-red-500 hover:border-red-400 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <h2 className="font-display text-[24px] font-300 text-[#2b2b2b] mb-4">Add User</h2>
          <form onSubmit={save} className="space-y-3">
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <input required type="text" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-[44px] border-[#e2d8c8]"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
            <button type="submit" className="w-full h-[50px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Create User</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
